import Vue from 'vue';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import {
    fetchTeams,
    createTeam,
    deleteTeam,
    updateTeamDetails,
    fetchTeamMembers,
    updateTeamMembers,
} from './teams.utils';

export default {
    namespaced: true,
    state: {
        error: null,
        fetching: false,
        map: {},
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { id, team }) {
            Vue.set(state.map, id, team);
        },
        deleteMapKey (state, { id }) {
            Vue.delete(state.map, id);
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        applyTeamsPatch (state, { user, patch }) {
            patch.forEach(({ op, value: teamIds }) => {
                switch (op) {
                    case 'add':
                        teamIds.forEach((teamId) => {
                            const members = state.map[teamId].members ?? [];
                            if (members.some((member) => member.id === user.id)) return;
                            members.push(user);
                            state.map[teamId].members = members;
                        });
                        break;
                    case 'remove':
                        teamIds.forEach((teamId) => {
                            state.map[teamId].members = state.map[teamId]?.members.filter(
                                (member) => member.id !== user.id,
                            );
                        });
                        break;
                }
            });
        },
    },
    actions: {
        async loadAll ({ state, commit }, { reFetch = false } = {}) {
            if (!isEmpty(state.map) && !reFetch) {
                return;
            }

            commit('setFetching', { fetching: true });

            try {
                const teamList = await fetchTeams();
                commit('setMap', { map: keyBy(teamList, 'id') });
            } catch (error) {
                commit('setError', { error });
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async createTeam ({ commit }, team) {
            commit('setFetching', { fetching: true });

            try {
                const newTeam = await createTeam(team);
                commit('setMapAtKey', {
                    id: newTeam.id,
                    team: newTeam,
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async deleteTeam ({ commit }, { id }) {
            commit('setFetching', { fetching: true });

            try {
                await deleteTeam(id);
                commit('deleteMapKey', { id });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async updateTeamDetails ({ commit, state }, team) {
            commit('setFetching', { fetching: true });

            try {
                const updatedTeam = await updateTeamDetails(team);

                const oldTeam = state.map[team.id];
                const mergedTeam = {
                    ...updatedTeam,
                    members: oldTeam?.members || [],
                };

                commit('setMapAtKey', {
                    id: updatedTeam.id,
                    team: mergedTeam,
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async loadTeamMembers ({ state, commit }, { id, reFetch = false }) {
            if (state.map[id]?.membersFetched && !reFetch) {
                return;
            }

            commit('setFetching', { fetching: true });

            try {
                const teamWithMembers = await fetchTeamMembers(id);
                const mergedTeam = {
                    ...teamWithMembers,
                    membersFetched: true,
                };
                commit('setMapAtKey', {
                    id,
                    team: mergedTeam,
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async updateTeamMembers ({ commit }, { id, members }) {
            commit('setFetching', { fetching: true });

            try {
                const updatedTeam = await updateTeamMembers(members, id);
                const mergedTeam = {
                    ...updatedTeam,
                    membersFetched: true,
                };
                commit('setMapAtKey', {
                    id,
                    team: mergedTeam,
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
    },
    getters: {
        list: (state) => values(state.map),
        teamById: (state) => (id) => state.map[id],
        teamByName: (state, getters) => (groupName) => {
            return getters.list.find((team) => team.groupName.toLowerCase().trim() === groupName.toLowerCase().trim());
        },
        showTeams: (state, getters, rootState, rootGetters) => {
            return (
                rootGetters['subscription/getFeatureFlag']('enableTeams') ||
                rootGetters['subscription/isScimEnabled']
            );
        },
        teamIdsByUserIdMap: (state, getters) => {
            const teamIdsByUserId = {};
            getters.list.forEach((team) => {
                team.members?.forEach((member) => {
                    const teamIds = teamIdsByUserId[member.id] ?? [];
                    teamIds.push(team.id);
                    teamIdsByUserId[member.id] = teamIds;
                });
            });

            return teamIdsByUserId;
        },
        teamsByUserId: (state, getters) => (userId) => {
            const teamIds = getters.teamIdsByUserIdMap[userId] ?? [];

            return teamIds.map((teamId) => state.map[teamId]);
        },
        isLoadingTeams: (state) => state.fetching,
    },
};
